import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Empty, Space, Spin, Tooltip, Typography } from 'antd'
import { ArrowLeftOutlined, CalendarOutlined, DownloadOutlined, MessageOutlined, ReloadOutlined } from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { useToast } from '@/contexts/ToastContext'
import { canCreateAgentBuilderDashboardDraft, hasAgentBuilderSavedViews } from '@/features/auth/permissions'
import { ApiRequestError } from '@/lib/httpClient'
import { downloadBlob } from '@/lib/download'
import { useAuthSession } from '@/lib/session'
import { resolveDrillOrder, type CatalogDimension } from '../drilldownHierarchy'
import type { AgentBuilderSavedDashboardMeta } from '../components/AgentBuilderPanel'
import AgentBuilderAccessGate from '../components/AgentBuilderAccessGate'
import {
  buildCatalogFromAgentBuilderCatalog,
  buildCatalogFromDashboardDefinition,
  normalizeDashboardDefinition,
  validateDashboard,
} from '../builderCore'
import { AgentBuilderDashboardInsights } from '../components/AgentBuilderDashboardInsights'
import { AgentBuilderPanel } from '../components/AgentBuilderPanel'
import { AnimatedAgentBuilderSidebar } from '../components/AnimatedAgentBuilderSidebar'
import { AgentBuilderScheduleModal } from '../components/AgentBuilderScheduleModal'
import { ManageViewsModal } from '../components/ManageViewsModal'
import { SavedViewsMenu } from '../components/SavedViewsMenu'
import { SaveViewModal } from '../components/SaveViewModal'
import {
  invalidateAgentBuilderDashboardConsumers,
  useAgentBuilderAccess,
  useAgentBuilderCatalogQuery,
  useAgentBuilderCreateDashboardViewMutation,
  useAgentBuilderDashboardQuery,
  useAgentBuilderDashboardViewsQuery,
  useAgentBuilderDeleteDashboardViewMutation,
  useAgentBuilderSetDefaultDashboardViewMutation,
  useAgentBuilderSettingsQuery,
  useAgentBuilderUpdateDashboardViewMutation,
} from '../hooks'
import { queryKeys } from '../queryKeys'
import { sanitizeDashboardSection } from '../sections'
import { appDashboardDataSource } from '../appDataSource'
import { DashboardDataSourceProvider } from '../dataSource'
import { selectIsViewModified } from './engine/selectors'
import { useDashboardViewEngine } from './engine/useDashboardViewEngine'
import {
  describeViewState,
  reconcileViewState,
  serializeViewParts,
  SKIPPED_VIEW_SETTINGS_MESSAGE,
  UNSUPPORTED_VIEW_MESSAGE,
  type ReconcileViewResult,
} from './savedViews'
import { DashboardBody } from './DashboardBody'
import { useDashboardFilterBehaviour, useDashboardRuntime } from './dashboardControls'
import { generateDashboardPdfBlob, getDashboardPdfFilename, type DashboardPdfSnapshot } from './pdfExport'
import { fetchFullTableData } from './pdfData'
import type { WidgetPresentationState } from '../vizModel'
import type {
  AgentBuilderChatMessage,
  AgentBuilderDashboardVersion,
  AgentBuilderDashboardView,
  DashboardDefinition,
  DashboardRuntimeState,
  DatePresetOption,
} from '../types'

const AGENT_SESSION_KEY = 'dashboard-builder.agent-chat-session.v1'

type DashboardViewerLocationState = {
  dashboard?: DashboardDefinition | null
  dashboardId?: string | null
  uploadedFileName?: string | null
}

function cloneSerializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function cloneWidgetSessions(widgetSessions: ReturnType<typeof useDashboardViewEngine>['widgetSessions']) {
  return cloneSerializable(widgetSessions)
}

function buildDashboardPdfSnapshot({
  dashboard,
  dashboardId,
  widgetSessions,
  runtimeState,
  dashboardInsightText,
  datePresetOptions,
  datePresetsAsOf,
  clientName,
  widgetPresentation,
}: {
  dashboard: DashboardDefinition
  dashboardId: string | null
  widgetSessions: ReturnType<typeof useDashboardViewEngine>['widgetSessions']
  runtimeState: DashboardRuntimeState
  dashboardInsightText: string | null
  datePresetOptions: DatePresetOption[]
  datePresetsAsOf: string | null
  clientName: string
  widgetPresentation: Record<string, WidgetPresentationState>
}): DashboardPdfSnapshot {
  return {
    dashboard: cloneSerializable(dashboard),
    dashboardId,
    widgetSessions: cloneWidgetSessions(widgetSessions),
    runtimeState: cloneSerializable(runtimeState),
    dashboardInsightText,
    datePresetOptions: cloneSerializable(datePresetOptions),
    datePresetsAsOf,
    clientName: clientName || null,
    widgetPresentation: cloneSerializable(widgetPresentation),
    capturedAt: new Date().toISOString(),
  }
}

// A 400 carries the API's own wording (duplicate name, view limit); anything else gets a generic message.
function savedViewErrorMessage(error: unknown, fallback: string) {
  return error instanceof ApiRequestError && error.status === 400 && error.message ? error.message : fallback
}

function DashboardViewerContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const session = useAuthSession()
  const viewerClientName = session?.currentClientName?.trim() ?? ''
  const { addToast } = useToast()
  const rootDashboardId = searchParams.get('rootDashboardId') || null
  const locationState = (location.state as DashboardViewerLocationState | null) ?? null
  const navigationDashboard = locationState?.dashboard ?? null
  const navigationDashboardId = locationState?.dashboardId ?? null
  const initialDashboardId = rootDashboardId ?? navigationDashboardId
  const clientId = session?.currentClientId ?? ''

  const [agentOpen, setAgentOpen] = useState(false)
  const [agentExpanded, setAgentExpanded] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [dashboardInsightsReadySignature, setDashboardInsightsReadySignature] = useState<string | null>(null)
  const [dashboardInsightsStale, setDashboardInsightsStale] = useState(false)
  const [dashboardInsightText, setDashboardInsightText] = useState<string | null>(null)
  const [agentMessages, setAgentMessages] = useState<AgentBuilderChatMessage[]>(() => {
    try {
      const raw = window.sessionStorage.getItem(AGENT_SESSION_KEY)
      return raw ? (JSON.parse(raw) as AgentBuilderChatMessage[]) : []
    } catch {
      return []
    }
  })
  const [agentChatBusy, setAgentChatBusy] = useState(false)

  // Saved views: personal named states of this dashboard. The default (or a ?view= link) is applied
  // before the first data load, so widgets are queried once.
  const { featureFlags, isReady: authorizationReady } = useAuthorization()
  const savedViewsEnabled = hasAgentBuilderSavedViews(featureFlags) && Boolean(initialDashboardId)
  const isDiAdmin = canCreateAgentBuilderDashboardDraft(session)
  const requestedViewId = searchParams.get('view')
  const savedDashboardQuery = useAgentBuilderDashboardQuery(initialDashboardId, clientId)
  const savedViewsRootId = savedDashboardQuery.data?.rootDashboardId
    ?? savedDashboardQuery.data?.dashboardId
    ?? initialDashboardId
  const viewsQuery = useAgentBuilderDashboardViewsQuery(clientId, savedViewsRootId, savedViewsEnabled)
  const settingsQuery = useAgentBuilderSettingsQuery(clientId, savedViewsEnabled && isDiAdmin, true)
  const savedViewsReadOnlyEmail = isDiAdmin ? settingsQuery.data?.impersonationEmail ?? null : null
  // Until a DI admin's settings arrive it isn't known whether the menu is read-only, so it stays loading.
  const savedViewsLoading = viewsQuery.isLoading || (isDiAdmin && settingsQuery.isLoading)
  const createView = useAgentBuilderCreateDashboardViewMutation(clientId)
  const updateView = useAgentBuilderUpdateDashboardViewMutation(clientId)
  const deleteView = useAgentBuilderDeleteDashboardViewMutation(clientId)
  const setDefaultView = useAgentBuilderSetDefaultDashboardViewMutation(clientId)
  const [activeViewId, setActiveViewId] = useState<string | null>(null)
  const [saveViewOpen, setSaveViewOpen] = useState(false)
  const [manageViewsOpen, setManageViewsOpen] = useState(false)
  const [busyViewId, setBusyViewId] = useState<string | null>(null)
  const savedViews = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data])
  const defaultView = savedDashboardQuery.data?.defaultView ?? null

  const initialSavedView = useMemo<AgentBuilderDashboardView | null>(() => {
    if (requestedViewId) return savedViews.find((view) => view.viewId === requestedViewId) ?? null
    return defaultView
  }, [defaultView, requestedViewId, savedViews])
  const initialView = useMemo(() => {
    // Flags arrive with the authorization context; hold the first load until it is known whether views are on.
    if (!authorizationReady && initialDashboardId) return { pending: true, view: null }
    if (!savedViewsEnabled) return undefined
    // isFetching: after a view change the cached dashboard (and its default view) is refetched on open.
    const pending = requestedViewId ? viewsQuery.isLoading : savedDashboardQuery.isFetching
    return { pending, view: pending ? null : initialSavedView?.state ?? null }
  }, [
    authorizationReady,
    initialDashboardId,
    initialSavedView,
    requestedViewId,
    savedDashboardQuery.isFetching,
    savedViewsEnabled,
    viewsQuery.isLoading,
  ])

  const replaceViewParam = useCallback((viewId: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (viewId) next.set('view', viewId)
      else next.delete('view')
      return next
    }, { replace: true, state: location.state })
  }, [location.state, setSearchParams])

  const reportViewResult = useCallback((result: ReconcileViewResult | null) => {
    if (!result) return
    if (result.unsupported) addToast(UNSUPPORTED_VIEW_MESSAGE, 'warning')
    else if (result.skipped.length > 0) addToast(SKIPPED_VIEW_SETTINGS_MESSAGE, 'warning')
  }, [addToast])

  const handleInitialViewApplied = useCallback((result: ReconcileViewResult) => {
    setActiveViewId(result.unsupported ? null : initialSavedView?.viewId ?? null)
    reportViewResult(result)
  }, [initialSavedView, reportViewResult])

  // A ?view= link to a view the caller doesn't own (or that was deleted) opens the defaults.
  // Only after the list loaded: a failed request must not drop the link.
  useEffect(() => {
    if (!savedViewsEnabled || !requestedViewId || !viewsQuery.isSuccess) return
    if (!savedViews.some((view) => view.viewId === requestedViewId)) {
      replaceViewParam(null)
    }
  }, [replaceViewParam, requestedViewId, savedViews, savedViewsEnabled, viewsQuery.isSuccess])

  const drillCatalogRef = useRef<CatalogDimension[]>([])
  const engine = useDashboardViewEngine({
    clientId,
    initialDashboard: navigationDashboard,
    initialDashboardId: initialDashboardId,
    onWarning: (message) => addToast(message, 'warning'),
    // Override orders resolve from the catalog alone; the global hierarchy is not needed.
    getWidgetDrillOrder: (widget) => resolveDrillOrder(widget, null, drillCatalogRef.current),
    initialView,
    onInitialViewApplied: handleInitialViewApplied,
  })

  const dashboard = engine.dashboard
  const dashboardId = engine.dashboardId
  const scheduleDashboardRootId = dashboard?.rootDashboardId ?? dashboard?.rootId ?? rootDashboardId ?? dashboardId
  const dashboardLoad = engine.state.dashboardLoad
  const { runtimeState: exportRuntimeState, selectedFilters: exportSelectedFilters } = useDashboardRuntime(engine)

  const catalogQuery = useAgentBuilderCatalogQuery(clientId, dashboard?.platformTypeId ?? 0)
  const dashboardCatalog = useMemo(() => dashboard ? buildCatalogFromDashboardDefinition(dashboard) : null, [dashboard])
  const backendCatalog = useMemo(
    () => dashboard && catalogQuery.data
      ? buildCatalogFromAgentBuilderCatalog(catalogQuery.data, dashboard.platformTypeId, 'Current Client')
      : null,
    [catalogQuery.data, dashboard],
  )
  const catalog = backendCatalog ?? dashboardCatalog
  // undefined while the catalog is loading; [] when it failed or the model publishes no presets.
  const loadedDatePresetOptions = catalogQuery.data?.datePresetOptions
  const catalogDatePresetOptions = useMemo(
    () => (catalogQuery.isLoading ? undefined : loadedDatePresetOptions ?? []),
    [catalogQuery.isLoading, loadedDatePresetOptions],
  )
  const datePresetOptions = useMemo(() => catalogDatePresetOptions ?? [], [catalogDatePresetOptions])
  const datePresetsAsOf = catalogQuery.data?.datePresetsAsOf ?? null
  drillCatalogRef.current = catalog?.find((platform) => platform.platformTypeId === dashboard?.platformTypeId)?.defaultDimensions
    ?? catalog?.[0]?.defaultDimensions
    ?? []
  const activeView = useMemo(
    () => savedViews.find((view) => view.viewId === activeViewId)
      ?? (defaultView?.viewId === activeViewId ? defaultView : null),
    [activeViewId, defaultView, savedViews],
  )
  const activeViewExpectedState = useMemo(() => {
    if (!activeView?.state || !dashboard || !catalogDatePresetOptions) return null
    const result = reconcileViewState(activeView.state, dashboard, catalogDatePresetOptions)
    return result.unsupported ? null : serializeViewParts(dashboard, result.state.filters, result.state.widgetQueries)
  }, [activeView, catalogDatePresetOptions, dashboard])
  const isActiveViewModified = engine.state.dashboardLoad.initialLoadCompleted
    && selectIsViewModified(engine.state, activeViewExpectedState)
  const saveViewSummary = useMemo(() => {
    if (!saveViewOpen || !dashboard) return []
    const state = engine.actions.captureViewState()
    return state ? describeViewState(state, dashboard, datePresetOptions) : []
  }, [dashboard, datePresetOptions, engine.actions, saveViewOpen])

  const selectSavedView = useCallback(async (view: AgentBuilderDashboardView | null) => {
    setActiveViewId(view?.viewId ?? null)
    replaceViewParam(view?.viewId ?? null)
    const result = await engine.actions.applyView(view?.state ?? null)
    if (result?.unsupported) {
      setActiveViewId(null)
      replaceViewParam(null)
    }
    reportViewResult(result)
  }, [engine.actions, replaceViewParam, reportViewResult])

  async function handleCreateView(values: { name: string; isDefault: boolean }) {
    const state = engine.actions.captureViewState()
    if (!state || !savedViewsRootId) return
    try {
      const view = await createView.mutateAsync({
        clientId,
        dashboardId: savedViewsRootId,
        name: values.name,
        state,
        isDefault: values.isDefault,
      })
      setActiveViewId(view.viewId)
      replaceViewParam(view.viewId)
      setSaveViewOpen(false)
      addToast('View saved', 'success')
    } catch (error) {
      throw new Error(savedViewErrorMessage(error, "Couldn't save the view. Try again."))
    }
  }

  async function handleUpdateActiveView() {
    const state = engine.actions.captureViewState()
    if (!state || !activeView || !savedViewsRootId) return
    try {
      await updateView.mutateAsync({ clientId, dashboardId: savedViewsRootId, viewId: activeView.viewId, state })
      addToast('View updated', 'success')
    } catch (error) {
      addToast(savedViewErrorMessage(error, "Couldn't save the view. Try again."), 'error')
    }
  }

  async function handleRenameView(view: AgentBuilderDashboardView, name: string) {
    if (!savedViewsRootId) return
    setBusyViewId(view.viewId)
    try {
      await updateView.mutateAsync({ clientId, dashboardId: savedViewsRootId, viewId: view.viewId, name })
      addToast('View updated', 'success')
    } catch (error) {
      throw new Error(savedViewErrorMessage(error, "Couldn't rename the view. Try again."))
    } finally {
      setBusyViewId(null)
    }
  }

  async function handleToggleDefaultView(view: AgentBuilderDashboardView) {
    if (!savedViewsRootId) return
    setBusyViewId(view.viewId)
    try {
      await setDefaultView.mutateAsync({ clientId, dashboardId: savedViewsRootId, viewId: view.isDefault ? null : view.viewId })
    } catch (error) {
      addToast(savedViewErrorMessage(error, "Couldn't change the default view. Try again."), 'error')
    } finally {
      setBusyViewId(null)
    }
  }

  async function handleDeleteView(view: AgentBuilderDashboardView) {
    if (!savedViewsRootId) return
    try {
      await deleteView.mutateAsync({ clientId, dashboardId: savedViewsRootId, viewId: view.viewId })
      if (view.viewId === activeViewId) {
        setActiveViewId(null)
        replaceViewParam(null)
      }
      addToast('View deleted', 'success')
    } catch (error) {
      addToast(savedViewErrorMessage(error, "Couldn't delete the view. Try again."), 'error')
    }
  }

  // A chat edit or restore resets filters to the new dashboard's defaults; keep the user's view on top.
  function reapplyActiveView() {
    if (!activeView?.state) return
    void engine.actions.applyView(activeView.state).then(reportViewResult)
  }

  const exportWidgetsReady = useMemo(() => {
    if (!dashboard || engine.hydratableWidgetIds.length === 0) return false

    return engine.hydratableWidgetIds.every((widgetId) => {
      const widgetSession = engine.widgetSessions[widgetId]
      return widgetSession?.status === 'live'
        && !widgetSession.request.inFlight
        && !widgetSession.request.queuedIntent
        // A sort change waits 1s before its request; the loaded rows are about to be replaced.
        && !widgetSession.ui.pendingSort
        && Boolean(widgetSession.data.lastUpdatedAt)
    })
  }, [dashboard, engine.hydratableWidgetIds, engine.widgetSessions])
  const canDownloadPdf =
    Boolean(dashboard) &&
    exportWidgetsReady &&
    Boolean(engine.insightSignature) &&
    dashboardInsightsReadySignature === engine.insightSignature &&
    !engine.hasPendingGlobalFilterChanges &&
    !isDownloadingPdf
  const pdfDisabledReason = !exportWidgetsReady
    ? 'Waiting for dashboard widgets to load.'
    : !engine.insightSignature || dashboardInsightsReadySignature !== engine.insightSignature
      ? dashboardInsightsStale
        ? 'Insights are out of date — click Re-generate to update them before exporting.'
        : 'Waiting for dashboard insights to finish.'
      : engine.hasPendingGlobalFilterChanges
        ? 'Apply or clear pending filters before downloading.'
        : undefined
  const handleDashboardInsightsReadyChange = useCallback((ready: boolean, signature: string | null, stale: boolean) => {
    setDashboardInsightsReadySignature(ready ? signature : null)
    setDashboardInsightsStale(stale)
  }, [])
  const filters = useDashboardFilterBehaviour(engine, catalogDatePresetOptions)
  // Card-local state each widget reports (trend granularity, expanded drilldown rows), read only by the PDF export.
  const widgetPresentationRef = useRef<Record<string, WidgetPresentationState>>({})
  const handleWidgetPresentationChange = useCallback((widgetId: string, state: WidgetPresentationState) => {
    widgetPresentationRef.current[widgetId] = state
  }, [])

  async function handleDownloadPdf() {
    if (!canDownloadPdf) return
    if (!dashboard) return

    setIsDownloadingPdf(true)
    try {
      const snapshot = buildDashboardPdfSnapshot({
        dashboard,
        dashboardId,
        widgetSessions: engine.widgetSessions,
        runtimeState: exportRuntimeState,
        dashboardInsightText,
        datePresetOptions,
        datePresetsAsOf,
        clientName: viewerClientName,
        widgetPresentation: widgetPresentationRef.current,
      })
      // Tables print every row: re-fetch any table that only holds one server page.
      const fullTableData = await fetchFullTableData({
        state: engine.state,
        clientId,
        widgetSessions: snapshot.widgetSessions,
      })
      snapshot.widgetSessions = fullTableData.widgetSessions
      snapshot.rowCoverage = fullTableData.rowCoverage
      if (fullTableData.failedWidgetIds.length > 0) {
        addToast('Some tables could not load all their rows; the PDF shows the rows already loaded.', 'warning')
      }
      const blob = await generateDashboardPdfBlob(snapshot)
      downloadBlob(blob, getDashboardPdfFilename(dashboard))
      addToast('Dashboard PDF downloaded.', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to generate dashboard PDF.', 'error')
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  function handleAgentDashboardUpdate(
    updated: DashboardDefinition,
    _savePrompt?: string,
    savedDashboard?: AgentBuilderSavedDashboardMeta,
  ) {
    if (!catalog) return false
    const rawValidation = validateDashboard(updated, catalog)
    if (!rawValidation.ok) {
      addToast(`Dashboard update rejected: ${rawValidation.errors.join('; ')}`, 'error')
      return false
    }

    const normalized = normalizeDashboardDefinition(updated, catalog)
    const normalizedWithSection = {
      ...normalized,
      section: sanitizeDashboardSection(normalized.section, normalized.platformTypeId),
    }
    const validation = validateDashboard(normalizedWithSection, catalog)
    if (!validation.ok) {
      addToast(`Dashboard update rejected: ${validation.errors.join('; ')}`, 'error')
      return false
    }

    const savedDashboardId = savedDashboard?.rootDashboardId ?? savedDashboard?.dashboardId ?? dashboardId
    engine.replaceDashboard(normalizedWithSection, savedDashboardId)
    reapplyActiveView()
    if (savedDashboardId) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, savedDashboardId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardVersions(clientId, savedDashboardId),
      })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, normalizedWithSection.platformTypeId)
    }
    return true
  }

  function handleAgentDashboardRestored(restored: DashboardDefinition, version: AgentBuilderDashboardVersion) {
    const restoredCatalog = buildCatalogFromDashboardDefinition(restored)
    const rawValidation = validateDashboard(restored, restoredCatalog)
    if (!rawValidation.ok) {
      addToast(`Dashboard restore rejected: ${rawValidation.errors.join('; ')}`, 'error')
      return false
    }

    const normalized = normalizeDashboardDefinition(restored, restoredCatalog)
    const restoredPlatformTypeId = version.platformTypeId || normalized.platformTypeId
    const restoredWithSection = {
      ...normalized,
      platformTypeId: restoredPlatformTypeId,
      section: sanitizeDashboardSection(version.section ?? normalized.section, restoredPlatformTypeId),
    }
    const validation = validateDashboard(restoredWithSection, restoredCatalog)
    if (!validation.ok) {
      addToast(`Dashboard restore rejected: ${validation.errors.join('; ')}`, 'error')
      return false
    }

    const restoredDashboardId = version.dashboardId ?? version.rootId ?? dashboardId
    engine.replaceDashboard(restoredWithSection, restoredDashboardId)
    reapplyActiveView()
    return true
  }

  useEffect(() => {
    window.sessionStorage.setItem(AGENT_SESSION_KEY, JSON.stringify(agentMessages))
  }, [agentMessages])

  if (!dashboard) {
    const isBootstrappingDashboard = Boolean(
      initialDashboardId
      && (!clientId || dashboardLoad.bootstrapStatus === 'loading'),
    )

    if (isBootstrappingDashboard) {
      return (
        <FeatureThemeProvider featureKey="pracIntel" withAntd className="p-6">
          <div className="min-h-[320px] flex items-center justify-center">
            <Spin tip="Loading dashboard..." size="large" />
          </div>
        </FeatureThemeProvider>
      )
    }

    if (dashboardLoad.bootstrapStatus === 'error') {
      return (
        <FeatureThemeProvider featureKey="pracIntel" withAntd className="p-6">
          <Alert
            type="error"
            showIcon
            message="Failed to load dashboard"
            description={dashboardLoad.error ?? 'The saved dashboard could not be loaded.'}
            action={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/dashboard-builder')}>Back to PracIntel</Button>}
          />
        </FeatureThemeProvider>
      )
    }

    return (
      <FeatureThemeProvider featureKey="pracIntel" withAntd className="p-6">
        <Empty description="No dashboard was provided for preview.">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/dashboard-builder')}>Back to PracIntel</Button>
        </Empty>
      </FeatureThemeProvider>
    )
  }

  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd className="agent-builder-viewer bg-[#eef1f6] px-10 pt-0 pb-6">
      <div
        className={`transition-[padding-right] duration-300 ease-out ${
          agentOpen ? (agentExpanded ? 'pr-[calc(50vw+1rem)]' : 'pr-[436px]') : 'pr-0'
        }`}
      >
        <Space direction="vertical" size={16} className="w-full">
        <div className="mb-5 px-1">
          {viewerClientName ? (
            <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-[1.6px] text-[#0f8f82]">
              {viewerClientName}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Typography.Title level={3} className="!m-0 !text-[28px] !font-bold !tracking-[-0.4px] !leading-tight !text-gray-900">
              {dashboard.title}
            </Typography.Title>
            <div className="flex flex-wrap gap-2">
              <Button className="ab-header-btn" size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/dashboard-builder')}>Back to PracIntel</Button>
              <Button
                className="ab-header-btn"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => {
                  addToast('Refreshing slicers and live widget data.', 'info')
                  void engine.actions.refreshAll()
                }}
              >
                Refresh Data
              </Button>
              <Button
                size="small"
                icon={<MessageOutlined />}
                aria-pressed={agentOpen}
                onClick={() => setAgentOpen((open) => !open)}
                disabled={!dashboard}
                className="ab-header-btn"
              >
                Chat
              </Button>
              <Button
                className="ab-header-btn"
                size="small"
                icon={<CalendarOutlined />}
                onClick={() => setScheduleOpen(true)}
                disabled={!scheduleDashboardRootId}
              >
                Schedule
              </Button>
              {/* Span wrapper so the tooltip still fires on the disabled button in every browser. */}
              <Tooltip title={canDownloadPdf ? undefined : pdfDisabledReason}>
                <span className="inline-block">
                  <Button
                    className="ab-header-btn"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => { void handleDownloadPdf() }}
                    disabled={!canDownloadPdf}
                    loading={isDownloadingPdf}
                  >
                    Download PDF
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
          {savedViewsEnabled && savedViewsRootId ? (
            <div className="mt-3">
              <SavedViewsMenu
                views={savedViews}
                loading={savedViewsLoading}
                activeView={activeView}
                isModified={isActiveViewModified}
                readOnlyEmail={savedViewsReadOnlyEmail}
                updating={updateView.isPending}
                onSelect={(view) => { void selectSavedView(view) }}
                onSaveNew={() => setSaveViewOpen(true)}
                onUpdate={() => { void handleUpdateActiveView() }}
                onManage={() => setManageViewsOpen(true)}
              />
            </div>
          ) : null}
        </div>

        <DashboardBody
          clientId={clientId}
          engine={engine}
          dashboard={dashboard}
          catalog={catalog}
          datePresetOptions={datePresetOptions}
          datePresetsAsOf={datePresetsAsOf}
          runtimeState={exportRuntimeState}
          selectedFilters={exportSelectedFilters}
          filters={filters}
          insights={
            <AgentBuilderDashboardInsights
              clientId={clientId}
              dashboardId={dashboardId}
              dashboard={dashboard}
              widgetSessions={engine.widgetSessions}
              insightSignature={engine.insightSignature}
              onReadyChange={handleDashboardInsightsReadyChange}
              onInsightReady={setDashboardInsightText}
            />
          }
          onWidgetPresentationChange={handleWidgetPresentationChange}
        />

        {dashboard && (
          <AnimatedAgentBuilderSidebar open={agentOpen} expanded={agentExpanded}>
            <AgentBuilderPanel
              clientId={clientId}
              dashboardId={dashboardId}
              currentDashboard={dashboard}
              catalog={catalog}
              platformTypeId={dashboard.platformTypeId}
              onDashboardUpdated={handleAgentDashboardUpdate}
              onDashboardRestored={handleAgentDashboardRestored}
              onClose={() => setAgentOpen(false)}
              expanded={agentExpanded}
              onExpandedChange={setAgentExpanded}
              messages={agentMessages}
              onMessagesChange={setAgentMessages}
              busy={agentChatBusy}
              onBusyChange={setAgentChatBusy}
              runtimeState={exportRuntimeState}
              slicerOptionsByField={engine.slicerOptionsByField}
            />
          </AnimatedAgentBuilderSidebar>
        )}
        <SaveViewModal
          open={saveViewOpen}
          saving={createView.isPending}
          summaryLines={saveViewSummary}
          currentDefaultName={savedViews.find((view) => view.isDefault)?.name ?? null}
          hasPendingFilterChanges={engine.hasPendingGlobalFilterChanges}
          onApplyFilters={() => void engine.actions.applyGlobalFilters()}
          onClose={() => setSaveViewOpen(false)}
          onSave={handleCreateView}
        />
        <ManageViewsModal
          open={manageViewsOpen}
          dashboardTitle={dashboard.title}
          views={savedViews}
          loading={viewsQuery.isLoading}
          busyViewId={busyViewId}
          onClose={() => setManageViewsOpen(false)}
          onSaveNew={() => {
            setManageViewsOpen(false)
            setSaveViewOpen(true)
          }}
          onRename={handleRenameView}
          onToggleDefault={(view) => { void handleToggleDefaultView(view) }}
          onDelete={(view) => { void handleDeleteView(view) }}
        />
        <AgentBuilderScheduleModal
          open={scheduleOpen}
          clientId={clientId}
          dashboardRootId={scheduleDashboardRootId}
          dashboardTitle={dashboard.title}
          widgets={dashboard.widgets}
          onClose={() => setScheduleOpen(false)}
        />
      </Space>
      </div>
    </FeatureThemeProvider>
  )
}

export default function DashboardViewerPage() {
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const access = useAgentBuilderAccess(clientId)

  if (!clientId || access.canAccess) {
    return (
      <DashboardDataSourceProvider value={appDashboardDataSource}>
        <DashboardViewerContent />
      </DashboardDataSourceProvider>
    )
  }

  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd className="p-6">
      <AgentBuilderAccessGate clientId={clientId}>{null}</AgentBuilderAccessGate>
    </FeatureThemeProvider>
  )
}
